import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { API } from '../service/ProductsService';
import { useAuth } from '../utils/AuthContext';
import { favorites, useFavoriteIds, parseFavorites, DROP_BADGE } from '../utils/favoritesStore';
import { basket } from '../utils/basketStore';
import ProductRow from './ProductRow';
import ProfileListModal from './ProfileListModal';
import DeleteAccountDialog from './DeleteAccountDialog';

// עמוד הפרופיל (רק למחובר - requiresLogin ב-MainPage): כרטיס משתמש + סטטיסטיקה, פרטים אישיים (עריכה),
// אבטחה (שינוי סיסמה) ומוצרים מועדפים. כל הפעולות ב-server/src/SQL/Profile.sql.
// active - הטאב גלוי (הטאבים נשארים טעונים ב-MainPage); הנתונים נטענים כשהוא נפתח.

const formatDate = (value) => {
  if (!value) return '';
  const d = new Date(value);
  return `${d.getDate()}.${d.getMonth() + 1}.${d.getFullYear()}`;
};
// 0501234567 → 050-1234567 (הטלפון נשמר כספרות בלבד - fnNormalizePhone)
const formatPhone = (phone) => (phone && /^0\d{9}$/.test(phone) ? `${phone.slice(0, 3)}-${phone.slice(3)}` : phone || '');
const digitsOnly = (phone) => String(phone || '').replace(/\D/g, '');

const s = {
  wrap:       { display: 'flex', flexDirection: 'column', gap: 20 },
  // כרטיס עליון
  hero:       { display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap', padding: 20, borderRadius: 16, color: '#fff', background: 'linear-gradient(135deg, #1a3f9c 0%, #2f5fd0 100%)', boxShadow: '0 6px 20px rgba(26,63,156,.18)' },
  avatar:     { width: 72, height: 72, flex: '0 0 72px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, fontWeight: 700, background: 'rgba(255,255,255,.18)', border: '2px solid rgba(255,255,255,.5)' },
  heroInfo:   { flex: '1 1 220px', minWidth: 0 },
  heroName:   { margin: 0, fontSize: 24, fontWeight: 700 },
  heroMeta:   { display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 },
  chip:       { fontSize: 13, padding: '3px 10px', borderRadius: 20, background: 'rgba(255,255,255,.16)', whiteSpace: 'nowrap' },
  stats:      { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10 },
  stat:       { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '12px 14px', textAlign: 'right', color: 'inherit', font: 'inherit', cursor: 'pointer' },
  statTop:    { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 },
  statMore:   { fontSize: 12, fontWeight: 600, color: '#1a3f9c' },
  statValue:  { fontSize: 24, fontWeight: 700 },
  statLabel:  { fontSize: 13, color: '#59636e' },
  // כרטיסים
  grid:       { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16, alignItems: 'start' },
  card:       { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 18 },
  cardHead:   { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 14 },
  cardTitle:  { margin: 0, fontSize: 18, fontWeight: 700 },
  cardSub:    { margin: '2px 0 0', fontSize: 13, color: '#59636e' },
  detail:     { display: 'flex', justifyContent: 'space-between', gap: 12, padding: '10px 0', borderBottom: '1px solid #f3f3f3' },
  detailKey:  { color: '#59636e' },
  detailVal:  { fontWeight: 600 },
  field:      { display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 12 },
  label:      { fontSize: 13, fontWeight: 600, color: '#1f2328' },
  input:      { padding: '10px 12px', fontSize: 16, border: '1px solid #d0d7de', borderRadius: 10, width: '100%' },
  hint:       { fontSize: 12, color: '#59636e' },
  row2:       { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 },
  check:      { display: 'flex', alignItems: 'center', gap: 8, margin: '4px 0 14px', fontSize: 15 },
  actions:    { display: 'flex', gap: 8, justifyContent: 'flex-start', flexWrap: 'wrap' },
  primary:    { padding: '10px 20px', fontSize: 15, fontWeight: 700, border: 'none', borderRadius: 10, background: '#1a3f9c', color: '#fff' },
  secondary:  { padding: '9px 16px', fontSize: 14, fontWeight: 600, border: '1px solid #d0d7de', borderRadius: 10, background: '#fff', color: '#1f2328' },
  outline:    { padding: '7px 14px', fontSize: 14, fontWeight: 600, border: '1px solid #1a3f9c', borderRadius: 10, background: '#fff', color: '#1a3f9c' },
  error:      { background: '#fff1f0', border: '1px solid #ffccc7', color: '#a8071a', padding: '10px 14px', borderRadius: 10, marginBottom: 12, fontSize: 14 },
  dangerCard: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', background: '#fff', border: '1px solid #ffccc7', borderRadius: 12, padding: 18 },
  dangerBtn:  { padding: '8px 16px', fontSize: 14, fontWeight: 600, border: '1px solid #cf222e', borderRadius: 10, background: '#fff', color: '#cf222e' },
  dropBanner: { background: '#dafbe1', border: '1px solid #aceebb', color: '#116329', padding: '10px 14px', borderRadius: 10, marginBottom: 12, fontSize: 14, fontWeight: 600 },
  success:    { background: '#effaf2', border: '1px solid #aceebb', color: '#1a7f37', padding: '10px 14px', borderRadius: 10, marginBottom: 12, fontSize: 14 },
  // מועדפים
  favHead:    { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 12 },
  favTools:   { display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' },
  filter:     { padding: '8px 12px', fontSize: 14, border: '1px solid #d0d7de', borderRadius: 10, minWidth: 200 },
  list:       { display: 'flex', flexDirection: 'column', gap: 10 },
  empty:      { textAlign: 'center', color: '#59636e', padding: '36px 16px', background: '#fff', borderRadius: 12, border: '1px dashed #d0d7de', lineHeight: 1.7 },
  emptyIcon:  { fontSize: 32, marginBottom: 6 },
  unavail:    { marginTop: 16 },
  unavailHead:{ fontSize: 14, fontWeight: 700, color: '#59636e', margin: '0 0 8px' },
  unavailRow: { display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: '#f6f8fa', borderRadius: 10, marginBottom: 6 },
  unavailName:{ flex: 1, minWidth: 0, color: '#59636e' },
  removeBtn:  { background: 'none', border: 'none', color: '#cf222e', fontSize: 13, fontWeight: 600, padding: 0 },
};

// ── פרטים אישיים: תצוגה / עריכה ──
const DetailsCard = ({ profile, onSaved }) => {
  const { updateUser } = useAuth();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const startEdit = () => {
    setForm({ firstName: profile.firstName, lastName: profile.lastName, phone: formatPhone(profile.phone),
              isKfarResident: !!profile.isKfarResident, password: '' });
    setError(null);
    setSuccess(null);
    setEditing(true);
  };

  const phoneChanged = form && digitsOnly(form.phone) !== profile.phone;
  const set = (key) => (e) => setForm({ ...form, [key]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });

  const save = async (e) => {
    e.preventDefault();
    // ולידציה בקליינט + שוב ב-spUpdateProfile
    if (!form.firstName.trim() || !form.lastName.trim()) { setError('נא להזין שם פרטי ושם משפחה'); return; }
    if (digitsOnly(form.phone).length < 9) { setError('מספר הטלפון לא תקין'); return; }
    if (phoneChanged && !form.password) { setError('כדי לשנות טלפון צריך להזין את הסיסמה'); return; }

    setSaving(true);
    setError(null);
    try {
      const resp = await API.updateProfile({
        firstName: form.firstName.trim(), lastName: form.lastName.trim(), phone: form.phone,
        isKfarResident: form.isKfarResident, password: phoneChanged ? form.password : undefined,
      });
      const status = resp?.[0]?.[0];
      if (status?.code === 0) {
        const updated = resp[1]?.[0] || {};
        updateUser({ firstName: updated.firstName, lastName: updated.lastName });   // השם בכותרת
        onSaved(updated);
        setEditing(false);
        setSuccess(phoneChanged ? 'הפרטים נשמרו. מעכשיו נכנסים עם מספר הטלפון החדש' : 'הפרטים נשמרו');
      } else if (status?.code !== 90) {
        setError(status?.msg || 'שגיאה בשמירה');
      }
    } catch (err) {
      setError(err.message || 'שגיאה בשמירה');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={s.card}>
      <div style={s.cardHead}>
        <div>
          <h2 style={s.cardTitle}>פרטים אישיים</h2>
          <p style={s.cardSub}>מספר הטלפון הוא שם המשתמש לכניסה</p>
        </div>
        {!editing && <button type="button" style={s.outline} onClick={startEdit}>✏️ עריכה</button>}
      </div>

      {success && !editing && <div style={s.success} role="status">{success}</div>}

      {!editing ? (
        <>
          <div style={s.detail}><span style={s.detailKey}>שם פרטי</span><span style={s.detailVal}>{profile.firstName}</span></div>
          <div style={s.detail}><span style={s.detailKey}>שם משפחה</span><span style={s.detailVal}>{profile.lastName}</span></div>
          <div style={s.detail}><span style={s.detailKey}>טלפון</span><span style={s.detailVal} className="num" dir="ltr">{formatPhone(profile.phone)}</span></div>
          <div style={{ ...s.detail, borderBottom: 'none' }}><span style={s.detailKey}>תושב/ת כפר חב"ד</span><span style={s.detailVal}>{profile.isKfarResident ? 'כן' : 'לא'}</span></div>
        </>
      ) : (
        <form onSubmit={save} noValidate>
          {error && <div style={s.error} role="alert">{error}</div>}
          <div style={s.row2}>
            <label style={s.field}>
              <span style={s.label}>שם פרטי</span>
              <input style={s.input} value={form.firstName} onChange={set('firstName')} maxLength={32} autoComplete="given-name" />
            </label>
            <label style={s.field}>
              <span style={s.label}>שם משפחה</span>
              <input style={s.input} value={form.lastName} onChange={set('lastName')} maxLength={32} autoComplete="family-name" />
            </label>
          </div>
          <label style={s.field}>
            <span style={s.label}>טלפון</span>
            <input style={s.input} value={form.phone} onChange={set('phone')} type="tel" inputMode="tel" dir="ltr" autoComplete="tel" />
          </label>
          {phoneChanged && (
            <label style={s.field}>
              <span style={s.label}>סיסמה נוכחית</span>
              <input style={s.input} value={form.password} onChange={set('password')} type="password" autoComplete="current-password" />
              <span style={s.hint}>שינוי טלפון משנה את שם המשתמש, ולכן צריך לאשר עם הסיסמה</span>
            </label>
          )}
          <label style={s.check}>
            <input type="checkbox" checked={form.isKfarResident} onChange={set('isKfarResident')} />
            תושב/ת כפר חב"ד
          </label>
          <div style={s.actions}>
            <button type="submit" style={s.primary} disabled={saving}>{saving ? 'שומר…' : 'שמירה'}</button>
            <button type="button" style={s.secondary} onClick={() => setEditing(false)} disabled={saving}>ביטול</button>
          </div>
        </form>
      )}
    </div>
  );
};

// ── אבטחה: שינוי סיסמה ──
const PasswordCard = () => {
  const empty = { current: '', next: '', confirm: '' };
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    if (!form.current || !form.next) { setError('נא להזין את הסיסמה הנוכחית ואת החדשה'); return; }
    if (form.next.length < 6) { setError('הסיסמה החדשה צריכה להכיל לפחות 6 תווים'); return; }
    if (form.next !== form.confirm) { setError('אימות הסיסמה לא תואם'); return; }

    setSaving(true);
    setError(null);
    try {
      const resp = await API.changePassword(form.current, form.next);
      const status = resp?.[0]?.[0];
      if (status?.code === 0) {
        const others = resp[1]?.[0]?.signedOutDevices || 0;
        setSuccess(others > 0 ? `הסיסמה שונתה. התנתקת מ-${others} מכשירים אחרים` : 'הסיסמה שונתה');
        setForm(empty);
        setOpen(false);
      } else if (status?.code !== 90) {
        setError(status?.msg || 'שגיאה בשינוי הסיסמה');
      }
    } catch (err) {
      setError(err.message || 'שגיאה בשינוי הסיסמה');
    } finally {
      setSaving(false);
    }
  };

  const type = show ? 'text' : 'password';

  return (
    <div style={s.card}>
      <div style={s.cardHead}>
        <div>
          <h2 style={s.cardTitle}>אבטחה</h2>
          <p style={s.cardSub}>אחרי שינוי סיסמה, מכשירים אחרים מתנתקים</p>
        </div>
        {!open && <button type="button" style={s.outline} onClick={() => { setOpen(true); setError(null); setSuccess(null); }}>🔒 שינוי סיסמה</button>}
      </div>

      {success && !open && <div style={s.success} role="status">{success}</div>}

      {open && (
        <form onSubmit={save} noValidate>
          {error && <div style={s.error} role="alert">{error}</div>}
          <label style={s.field}>
            <span style={s.label}>סיסמה נוכחית</span>
            <input style={s.input} type={type} value={form.current} onChange={set('current')} autoComplete="current-password" />
          </label>
          <label style={s.field}>
            <span style={s.label}>סיסמה חדשה</span>
            <input style={s.input} type={type} value={form.next} onChange={set('next')} autoComplete="new-password" />
            <span style={s.hint}>לפחות 6 תווים</span>
          </label>
          <label style={s.field}>
            <span style={s.label}>אימות סיסמה חדשה</span>
            <input style={s.input} type={type} value={form.confirm} onChange={set('confirm')} autoComplete="new-password" />
          </label>
          <label style={s.check}>
            <input type="checkbox" checked={show} onChange={(e) => setShow(e.target.checked)} />
            הצגת הסיסמאות
          </label>
          <div style={s.actions}>
            <button type="submit" style={s.primary} disabled={saving}>{saving ? 'שומר…' : 'שינוי סיסמה'}</button>
            <button type="button" style={s.secondary} onClick={() => { setOpen(false); setForm(empty); }} disabled={saving}>ביטול</button>
          </div>
        </form>
      )}
    </div>
  );
};

// ── מוצרים מועדפים ──
const FavoritesSection = ({ active }) => {
  const favoriteIds = useFavoriteIds();
  const [data, setData] = useState(null);     // parseFavorites: { stores, items, pricesByItem, unavailable, dropsByItem }
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('');
  const [added, setAdded] = useState(null);
  const requestId = useRef(0);

  const load = useCallback(async () => {
    const id = ++requestId.current;
    setLoading(true);
    setError(null);
    try {
      const resp = await API.getFavorites();
      if (id !== requestId.current) return;
      const status = resp?.[0]?.[0];
      if (status?.code === 0) {
        setData(parseFavorites(resp));
      } else if (status?.code !== 90) {
        setError(status?.msg || 'שגיאה בטעינת המועדפים');
      }
    } catch (err) {
      if (id === requestId.current) setError(err.message || 'שגיאה בטעינת המועדפים');
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, []);

  // הסרה (❤ בשורה) - המוצר נעלם מיד, בלי טעינה. מוצר שנוסף בחיפוש ועוד לא כאן - טוענים מחדש כשהטאב גלוי
  const loadedIds = useMemo(
    () => new Set(data ? [...data.items.map((x) => x.id), ...data.unavailable.map((x) => x.id)] : []),
    [data],
  );
  const hasNew = [...favoriteIds].some((id) => !loadedIds.has(id));

  useEffect(() => {
    if (active && (!data || hasNew) && !loading) load();
  }, [active, hasNew]); // eslint-disable-line react-hooks/exhaustive-deps

  // עד שה-ids נטענו (favoritesStore) - מציגים את מה שהשרת החזיר כמו שהוא
  const stillFavorite = (x) => !favorites.isLoaded() || favoriteIds.has(x.id);
  // מוצרים שהוזלו - ראשונים
  const dropsByItem = data?.dropsByItem || {};
  const items = (data?.items || []).filter(stillFavorite)
    .sort((a, b) => (dropsByItem[b.id] ? 1 : 0) - (dropsByItem[a.id] ? 1 : 0));
  const dropCount = items.filter((x) => dropsByItem[x.id]).length;
  const unavailable = (data?.unavailable || []).filter(stillFavorite);
  const q = filter.trim().toLowerCase();
  const shown = q
    ? items.filter((x) => `${x.name} ${x.brandName || ''}`.toLowerCase().includes(q))
    : items;

  // השרת מוסיף את המועדפים שעוד לא ברשימה (spSaveShoppingListItems, source = favorites)
  const addAllToBasket = async () => {
    const count = await basket.addFavorites();
    if (count === null) return;
    setAdded(count ? `${count} מוצרים נוספו לרשימת הקניות` : 'כל המועדפים כבר ברשימת הקניות');
    setTimeout(() => setAdded(null), 3500);
  };

  const remove = async (id) => {
    const result = await favorites.set(id, false);
    if (!result.ok) setError(result.msg);
  };

  return (
    <section>
      <div style={s.favHead}>
        <div>
          <h2 style={s.cardTitle}>המוצרים המועדפים שלי {items.length > 0 && `(${items.length})`}</h2>
          <p style={s.cardSub}>המחירים העדכניים בכל החנויות. להוספה - לחיצה על 🤍 ליד כל מוצר באתר</p>
        </div>
        {items.length > 0 && (
          <div style={s.favTools}>
            {items.length > 5 && (
              <input style={s.filter} type="search" value={filter} onChange={(e) => setFilter(e.target.value)}
                     placeholder="חיפוש במועדפים" aria-label="חיפוש במועדפים" />
            )}
            <button type="button" style={s.outline} onClick={addAllToBasket}>🛒 הכל לרשימת הקניות</button>
          </div>
        )}
      </div>

      {dropCount > 0 && (
        <div style={s.dropBanner} role="status">
          ⬇ {dropCount === 1 ? 'מוצר מועדף אחד הוזל' : `${dropCount} מוצרים מועדפים הוזלו`} בשבוע האחרון - הם מופיעים ראשונים
        </div>
      )}
      {added && <div style={s.success} role="status">{added}</div>}
      {error && <div style={s.error} role="alert">{error}</div>}

      {!data && loading && <div style={s.empty}>טוען…</div>}

      {data && items.length === 0 && unavailable.length === 0 && (
        <div style={s.empty}>
          <div style={s.emptyIcon}>🤍</div>
          עוד אין מוצרים מועדפים.<br />
          לוחצים על 🤍 ליד מוצר בחיפוש, והוא יחכה כאן - עם המחיר העדכני בכל חנות.
        </div>
      )}

      {shown.length > 0 && (
        <div style={s.list}>
          {shown.map((item) => (
            <ProductRow key={item.id} item={item} stores={data.stores} prices={data.pricesByItem[item.id] || {}}
                        badge={dropsByItem[item.id] ? DROP_BADGE : null} drops={dropsByItem[item.id]} />
          ))}
        </div>
      )}
      {q && items.length > 0 && shown.length === 0 && <div style={s.empty}>אין מועדפים שמתאימים ל"{filter}"</div>}

      {unavailable.length > 0 && (
        <div style={s.unavail}>
          <h3 style={s.unavailHead}>לא נמכרים כרגע באף חנות</h3>
          {unavailable.map((x) => (
            <div key={x.id} style={s.unavailRow}>
              <span style={s.unavailName}>{x.name || `מוצר ${x.id}`}{x.brandName ? ` · ${x.brandName}` : ''}</span>
              <button type="button" style={s.removeBtn} onClick={() => remove(x.id)}>הסרה</button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

const ProfileTab = ({ active }) => {
  const { user } = useAuth();
  const favoriteIds = useFavoriteIds();
  const [profile, setProfile] = useState(null);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);
  const [openList, setOpenList] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const closeDelete = useCallback(() => setDeleteOpen(false), []);   // type של הרשימה שפתוחה בחלון
  // useCallback: החלון נרשם ל-Escape לפי onClose. בסגירה - המספרים מתעדכנים (למשל אחרי "חיפוש שוב")
  const closeList = useCallback(() => { setOpenList(null); load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(async () => {
    try {
      const resp = await API.getProfile();
      const status = resp?.[0]?.[0];
      if (status?.code === 0) {
        setProfile(resp[1]?.[0] || null);
        setStats(resp[2]?.[0] || null);
        setError(null);
      } else if (status?.code !== 90) {
        setError(status?.msg || 'שגיאה בטעינת הפרופיל');
      }
    } catch (err) {
      setError(err.message || 'שגיאה בטעינת הפרופיל');
    }
  }, []);

  // בכל פתיחה של הטאב - הסטטיסטיקה (חיפושים, עדכוני מחיר) משתנה בזמן הגלישה
  useEffect(() => {
    if (active && user) load();
  }, [active, user, load]);

  if (!user) return null;

  if (!profile) {
    return error
      ? <div style={s.error} role="alert">{error}</div>
      : <div style={s.empty}>טוען את הפרופיל…</div>;
  }

  const initials = `${profile.firstName?.[0] || ''}${profile.lastName?.[0] || ''}`;
  // לחיצה על מספר פותחת את הרשימה שמאחוריו (ProfileListModal). type - ר' PROFILE_LISTS
  const statTiles = [
    { type: 'favorites', label: 'מוצרים מועדפים', value: favoriteIds.size },   // חי - משתנה עם ה-❤
    { type: 'searches', label: 'חיפושים', value: stats?.searchCount ?? 0 },
    { type: 'priceReports', label: 'עדכוני מחיר', value: stats?.priceReportCount ?? 0 },
    { type: 'feedback', label: 'פידבקים ששלחת', value: stats?.feedbackCount ?? 0 },
  ];

  return (
    <div style={s.wrap}>
      <div style={s.hero}>
        <div style={s.avatar} aria-hidden="true">{initials}</div>
        <div style={s.heroInfo}>
          <h2 style={s.heroName}>{profile.firstName} {profile.lastName}</h2>
          <div style={s.heroMeta}>
            <span style={s.chip} className="num" dir="ltr">📱 {formatPhone(profile.phone)}</span>
            {profile.isKfarResident && <span style={s.chip}>🏡 תושב/ת כפר חב"ד</span>}
            <span style={s.chip}>חבר/ה מאז {formatDate(profile.registeredAt)}</span>
          </div>
        </div>
      </div>

      <div style={s.stats}>
        {statTiles.map((t) => (
          <button key={t.type} type="button" style={s.stat} onClick={() => setOpenList(t.type)} aria-label={`${t.label}: ${t.value} - הצגת הרשימה`}>
            <div style={s.statTop}>
              <span style={s.statValue} className="num">{Number(t.value).toLocaleString('he-IL')}</span>
              <span style={s.statMore} aria-hidden="true">הצגה ‹</span>
            </div>
            <div style={s.statLabel}>{t.label}</div>
          </button>
        ))}
      </div>
      {openList && <ProfileListModal type={openList} onClose={closeList} />}

      <div style={s.grid}>
        <DetailsCard profile={profile} onSaved={(updated) => setProfile({ ...profile, ...updated })} />
        <PasswordCard />
      </div>

      <FavoritesSection active={active} />

      {/* מחיקת חשבון - זכות המחיקה לפי חוק הגנת הפרטיות (spDeleteAccount) */}
      <div style={s.dangerCard}>
        <div>
          <h2 style={s.cardTitle}>מחיקת החשבון</h2>
          <p style={s.cardSub}>מחיקת כל הפרטים האישיים, המועדפים ורשימת הקניות. פעולה סופית.</p>
        </div>
        <button type="button" style={s.dangerBtn} onClick={() => setDeleteOpen(true)}>מחיקת החשבון…</button>
      </div>
      {deleteOpen && <DeleteAccountDialog onClose={closeDelete} />}
    </div>
  );
};

export default ProfileTab;
